import authService from './authService';
import { ensureOk, readData, readList, readPaginated, toQuery } from './apiResponse';
import type { Activity, ActivityFilter, ActivityFormData, ActivityHistory, CalendarResponse, PaginatedList } from '../types/activity';

class ActivityService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  /**
   * Lista paginada com filtros (só do usuário logado)
   * GET /activities
   */
  async getAll(filter: ActivityFilter = {}): Promise<PaginatedList<Activity>> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(
      `/activities${toQuery({
        board_id: filter.board_id,
        board_column_id: filter.board_column_id,
        priority: filter.priority,
        status: filter.status,
        due_from: filter.due_from,
        due_to: filter.due_to,
        no_due_date: filter.no_due_date,
        overdue: filter.overdue,
        search: filter.search,
        page: filter.page,
        page_size: filter.page_size,
        sort_by: filter.sort_by,
        sort_direction: filter.sort_direction,
      })}`,
    );
    return readPaginated<Activity>(response, 'Falha ao buscar as atividades');
  }

  /**
   * Calendário/cronograma do período
   * GET /activities/calendar?start=&end=&board_id=
   */
  async getCalendar(params: { start: string; end: string; boardId?: number }): Promise<CalendarResponse> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(
      `/activities/calendar${toQuery({ start: params.start, end: params.end, board_id: params.boardId })}`,
    );
    const data = await readData<CalendarResponse>(response, 'Falha ao carregar o calendário');
    return {
      start: data?.start ?? params.start,
      end: data?.end ?? params.end,
      activities: data?.activities ?? [],
      no_due_date: data?.no_due_date ?? [],
      boards: data?.boards ?? [],
    };
  }

  /** GET /activities/:id */
  async getById(id: number): Promise<Activity> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/activities/${id}`);
    return readData<Activity>(response, 'Falha ao buscar a atividade');
  }

  /** GET /activities/:id/history */
  async getHistory(id: number): Promise<ActivityHistory[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/activities/${id}/history`);
    return readList<ActivityHistory>(response, 'Falha ao buscar o histórico');
  }

  /** POST /activities (sem coluna → primeira coluna aberta) */
  async create(formData: ActivityFormData): Promise<Activity> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/activities', formData);
    return readData<Activity>(response, 'Falha ao criar a atividade');
  }

  /** PUT /activities/:id */
  async update(id: number, formData: ActivityFormData): Promise<Activity> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/activities/${id}`, formData);
    return readData<Activity>(response, 'Falha ao atualizar a atividade');
  }

  /**
   * Drag-and-drop do kanban. Mover para coluna "concluído" conclui; sair dela reabre.
   * PATCH /activities/:id/move
   */
  async move(id: number, boardColumnId: number, sortOrder?: number): Promise<Activity> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.patch(`/activities/${id}/move`, {
      board_column_id: boardColumnId,
      ...(sortOrder !== undefined ? { sort_order: sortOrder } : {}),
    });
    return readData<Activity>(response, 'Falha ao mover a atividade');
  }

  /** POST /activities/:id/complete */
  async complete(id: number): Promise<Activity> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/activities/${id}/complete`);
    return readData<Activity>(response, 'Falha ao concluir a atividade');
  }

  /** POST /activities/:id/reopen */
  async reopen(id: number): Promise<Activity> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/activities/${id}/reopen`);
    return readData<Activity>(response, 'Falha ao reabrir a atividade');
  }

  /** DELETE /activities/:id */
  async delete(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/activities/${id}`);
    await ensureOk(response, 'Falha ao excluir a atividade');
  }
}

export const activityService = new ActivityService();
export default activityService;
